import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, Application, Profile } from '../../lib/supabase';
import { Shield, CheckCircle2, MessageSquare, User, ChevronRight, Clock, TrendingUp, Star, Award, Calendar, ArrowRight } from 'lucide-react';

export const JobManagement: React.FC = () => {
  const { profile } = useAuth();
  const [applications, setApplications] = useState<{
    new: Application[];
    contacted: Application[];
    verified: Application[];
    hired: Application[];
  }>({
    new: [],
    contacted: [],
    verified: [],
    hired: [],
  });
  const [contractors, setContractors] = useState<Map<string, Profile>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadApplications();
  }, [profile]);

  const loadApplications = async () => {
    if (!profile) return;

    try {
      const { data: jobsData } = await supabase
        .from('jobs')
        .select('id')
        .eq('gc_id', profile.id);

      if (!jobsData || jobsData.length === 0) {
        setLoading(false);
        return;
      }

      const jobIds = jobsData.map(j => j.id);

      const { data: appsData, error } = await supabase
        .from('applications')
        .select('*, jobs(*)')
        .in('job_id', jobIds)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const apps = appsData || [];
      const contractorIds = [...new Set(apps.map(a => a.contractor_id))];

      const { data: profilesData } = await supabase
        .from('profiles')
        .select('*')
        .in('id', contractorIds);

      const profileMap = new Map<string, Profile>();
      (profilesData || []).forEach(p => profileMap.set(p.id, p));
      setContractors(profileMap);

      setApplications({
        new: apps.filter(a => a.status === 'new'),
        contacted: apps.filter(a => a.status === 'contacted'),
        verified: apps.filter(a => a.status === 'verified'),
        hired: apps.filter(a => a.status === 'hired'),
      });
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateApplicationStatus = async (appId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('applications')
        .update({ status: newStatus })
        .eq('id', appId);

      if (error) throw error;
      await loadApplications();
    } catch (error) {
      console.error('Error updating application:', error);
    }
  };

  const getTimeAgo = (date: string) => {
    const now = new Date();
    const past = new Date(date);
    const diffInHours = Math.floor((now.getTime() - past.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return { text: 'Just now', color: 'text-green-600' };
    if (diffInHours < 24) return { text: `${diffInHours}h ago`, color: 'text-green-600' };
    if (diffInHours < 48) return { text: 'Yesterday', color: 'text-amber-600' };
    const days = Math.floor(diffInHours / 24);
    return { text: `${days}d ago`, color: days > 7 ? 'text-red-600' : 'text-slate-600' };
  };

  const ApplicationCard: React.FC<{ app: Application }> = ({ app }) => {
    const contractor = contractors.get(app.contractor_id);
    const job = (app as any).jobs;
    const timeAgo = getTimeAgo(app.created_at);
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div
        className="group bg-white border border-slate-200 rounded-xl p-4 hover:shadow-lg hover:border-amber-300 hover:scale-[1.02] transition-all duration-200 cursor-pointer"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex items-start space-x-3 mb-3">
          <div className="relative">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center flex-shrink-0 ring-2 ring-white shadow-sm">
              <span className="text-lg font-bold text-amber-700">
                {contractor?.full_name?.charAt(0) || 'U'}
              </span>
            </div>
            {contractor?.verification_status === 'verified' && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center ring-2 ring-white">
                <CheckCircle2 className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-slate-900 truncate group-hover:text-amber-600 transition-colors">
                {contractor?.full_name || 'Unknown'}
              </h4>
              <div className={`flex items-center text-xs font-medium ${timeAgo.color}`}>
                <Clock className="w-3 h-3 mr-1" />
                {timeAgo.text}
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600 mb-2">
              <span className="px-2 py-0.5 bg-slate-100 rounded-full font-medium">{contractor?.trade_type}</span>
              <span className="flex items-center">
                <Star className="w-3 h-3 mr-0.5 text-amber-500 fill-amber-500" />
                {contractor?.rating.toFixed(1)}
              </span>
              <span className="flex items-center">
                <Award className="w-3 h-3 mr-0.5 text-slate-400" />
                {contractor?.jobs_completed || 0} jobs
              </span>
            </div>
            <p className="text-xs text-slate-500 truncate flex items-center">
              <ArrowRight className="w-3 h-3 mr-1" />
              {job?.title}
            </p>
          </div>
        </div>

        {app.message && (
          <div className="bg-slate-50 rounded-lg p-3 mb-3 border border-slate-100">
            <p className="text-sm text-slate-700 line-clamp-2">{app.message}</p>
          </div>
        )}

        <div className={`flex items-center gap-2 transition-all duration-200 ${isHovered ? 'opacity-100' : 'opacity-0 h-0 overflow-hidden'}`}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              updateApplicationStatus(app.id, 'contacted');
            }}
            className="flex-1 px-3 py-2 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-all hover:scale-105 flex items-center justify-center"
          >
            <MessageSquare className="w-3 h-3 mr-1" />
            Contact
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              updateApplicationStatus(app.id, 'verified');
            }}
            className="flex-1 px-3 py-2 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg transition-all hover:scale-105 flex items-center justify-center"
          >
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Verify
          </button>
        </div>
      </div>
    );
  };

  const getColumnGradient = (title: string) => {
    switch (title.toLowerCase()) {
      case 'new':
        return 'from-amber-500 to-amber-600';
      case 'contacted':
        return 'from-blue-500 to-blue-600';
      case 'verified':
        return 'from-green-500 to-green-600';
      case 'hired':
        return 'from-emerald-500 to-emerald-600';
      default:
        return 'from-slate-500 to-slate-600';
    }
  };

  const Column: React.FC<{ title: string; apps: Application[]; icon?: React.ReactNode }> = ({ title, apps, icon }) => {
    const totalApps = applications.new.length + applications.contacted.length + applications.verified.length + applications.hired.length;
    const percentage = totalApps > 0 ? Math.round((apps.length / totalApps) * 100) : 0;

    return (
      <div className="flex-1 min-w-[280px] max-w-[340px] snap-center">
        <div className={`bg-gradient-to-r ${getColumnGradient(title)} rounded-xl p-4 mb-3 shadow-md`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-white flex items-center text-lg">
              {icon}
              {title}
            </h3>
            <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-sm font-bold rounded-full border border-white/30">
              {apps.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white/50 rounded-full transition-all duration-500"
                style={{ width: `${percentage}%` }}
              ></div>
            </div>
            <span className="text-xs text-white/90 font-medium">{percentage}%</span>
          </div>
        </div>
        <div className="space-y-3 min-h-[200px]">
          {apps.map(app => (
            <ApplicationCard key={app.id} app={app} />
          ))}
          {apps.length === 0 && (
            <div className="text-center py-12 px-4">
              <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center">
                {icon}
              </div>
              <p className="text-slate-400 text-sm font-medium">No {title.toLowerCase()} applications</p>
              <p className="text-slate-300 text-xs mt-1">Applications will appear here</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-24 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl animate-pulse"></div>
        <div className="flex gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex-1 space-y-3">
              <div className="h-16 bg-slate-100 rounded-lg animate-pulse"></div>
              <div className="space-y-2">
                <div className="h-32 bg-slate-50 rounded-lg animate-pulse"></div>
                <div className="h-32 bg-slate-50 rounded-lg animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900 rounded-2xl p-8 overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="relative">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2 flex items-center">
                Manage Jobs
                <span className="ml-3 px-3 py-1 bg-white/10 backdrop-blur-sm text-white text-sm font-medium rounded-full border border-white/20">
                  {applications.new.length + applications.contacted.length + applications.verified.length + applications.hired.length} Total
                </span>
              </h1>
              <p className="text-slate-300">Track applications through your hiring pipeline</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/20">
                <div className="text-xs text-slate-300 mb-1">Conversion Rate</div>
                <div className="text-xl font-bold text-white flex items-center">
                  <TrendingUp className="w-4 h-4 mr-1 text-green-400" />
                  {applications.new.length + applications.contacted.length + applications.verified.length + applications.hired.length > 0
                    ? Math.round((applications.hired.length / (applications.new.length + applications.contacted.length + applications.verified.length + applications.hired.length)) * 100)
                    : 0}%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-6 overflow-x-auto pb-6 px-1 snap-x snap-mandatory md:snap-none">
        <Column
          title="New"
          apps={applications.new}
          icon={<ChevronRight className="w-5 h-5 mr-2" />}
        />
        <Column
          title="Contacted"
          apps={applications.contacted}
          icon={<MessageSquare className="w-5 h-5 mr-2" />}
        />
        <Column
          title="Verified"
          apps={applications.verified}
          icon={<Shield className="w-5 h-5 mr-2" />}
        />
        <Column
          title="Hired"
          apps={applications.hired}
          icon={<CheckCircle2 className="w-5 h-5 mr-2" />}
        />
      </div>
    </div>
  );
};
